import { z } from "zod";
import { Localized } from "./localized.ts";
import { ContentLanguage, ContentLanguageSource } from "./translation.ts";

/** 0 = none, 1 = P0 (urgent) ... 4 = P3 (low). */
export const Priority = z.number().int().min(0).max(4);
export type Priority = z.infer<typeof Priority>;

export const PRIORITY_NONE = 0;
const PRIORITY_WORDS = ["none", "urgent", "high", "medium", "low"] as const;

/** Parses "p0".."p3", "urgent".."low", "none" or "0".."4" (stored value). */
export function parsePriority(input: string): Priority | null {
  const v = input.trim().toLowerCase();
  const p = /^p([0-3])$/.exec(v);
  if (p) return Number(p[1]) + 1;
  const word = (PRIORITY_WORDS as readonly string[]).indexOf(v);
  if (word >= 0) return word;
  if (/^[0-4]$/.test(v)) return Number(v);
  return null;
}

/** Urgency rank: none = 0, P3 = 1 ... P0 = 4. Higher means more urgent; used for comparisons and sorting. */
export function priorityUrgency(priority: number): number {
  return priority === 0 ? 0 : 5 - priority;
}

export const STATUS_CATEGORIES = ["backlog", "unstarted", "started", "completed", "canceled"] as const;
export const StatusCategory = z.enum(STATUS_CATEGORIES);
export type StatusCategory = z.infer<typeof StatusCategory>;
export const CLOSED_CATEGORIES: readonly StatusCategory[] = ["completed", "canceled"];
export function isClosedCategory(category: StatusCategory): boolean {
  return category === "completed" || category === "canceled";
}

export const RELATION_KINDS = ["blocks", "relates", "duplicates"] as const;
export const RelationKind = z.enum(RELATION_KINDS);
export type RelationKind = z.infer<typeof RelationKind>;

export const MILESTONE_STATUSES = ["open", "released", "closed"] as const;
export const MilestoneStatus = z.enum(MILESTONE_STATUSES);
export type MilestoneStatus = z.infer<typeof MilestoneStatus>;

export const ItemId = z.uuid();
export const UserId = z.string().min(1);
/** Calendar date "YYYY-MM-DD". */
export const DateOnly = z.iso.date();
/** ISO 8601 timestamp string. */
export const Timestamp = z.string();

/** Custom field values keyed by field slug. */
export const FieldValues = z.record(z.string(), z.unknown());

/**
 * What the client caches per item (sync-lite). Small, no description.
 * `blockedByIds` lists items with a `blocks` relation onto this one (open or closed);
 * clients decide "blocked" by checking whether any of those is still open.
 */
export const ItemLite = z.object({
  id: ItemId,
  key: z.string(),
  productId: z.uuid(),
  spaceId: z.uuid(),
  number: z.number().int(),
  typeId: z.uuid(),
  statusId: z.uuid(),
  priority: Priority,
  title: z.string(),
  parentId: z.uuid().nullable(),
  milestoneId: z.uuid().nullable(),
  rank: z.string(),
  assigneeIds: z.array(UserId),
  labelIds: z.array(z.uuid()),
  blockedByIds: z.array(z.uuid()),
  estimate: z.number().nullable(),
  startDate: DateOnly.nullable(),
  dueDate: DateOnly.nullable(),
  fields: FieldValues,
  reporterId: UserId.nullable(),
  createdById: UserId.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  statusChangedAt: Timestamp.nullable(),
  startedAt: Timestamp.nullable(),
  completedAt: Timestamp.nullable(),
  archivedAt: Timestamp.nullable(),
  version: z.number().int(),
  /** Language the content is written in; null = the product's content language. Always sent by the server. */
  contentLanguage: ContentLanguage.nullable().optional(),
  /** How `contentLanguage` was set: detected (`auto`) or chosen by a person (`manual`, never overridden). */
  contentLanguageSource: ContentLanguageSource.nullable().optional(),
});
export type ItemLite = z.infer<typeof ItemLite>;

/** Payload of delete events: the client drops the row. */
export const ItemTombstone = z.object({ id: ItemId, productId: z.uuid(), deleted: z.literal(true) });
export type ItemTombstone = z.infer<typeof ItemTombstone>;

export const UserSummary = z.object({
  id: UserId,
  name: z.string(),
  image: z.string().nullable(),
  kind: z.enum(["human", "bot"]),
});
export type UserSummary = z.infer<typeof UserSummary>;

export const ItemSummary = z.object({
  id: ItemId,
  key: z.string(),
  title: z.string(),
  typeId: z.uuid(),
  statusId: z.uuid(),
  statusCategory: StatusCategory,
  priority: Priority,
  deleted: z.boolean(),
});
export type ItemSummary = z.infer<typeof ItemSummary>;

export const TypeSummary = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: Localized,
  icon: z.string(),
  color: z.string(),
  level: z.number().int(),
});
export type TypeSummary = z.infer<typeof TypeSummary>;

export const StatusSummary = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: Localized,
  category: StatusCategory,
  color: z.string(),
});
export type StatusSummary = z.infer<typeof StatusSummary>;

export const LabelSummary = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: Localized,
  color: z.string(),
  groupId: z.uuid().nullable(),
  groupSlug: z.string().nullable(),
});
export type LabelSummary = z.infer<typeof LabelSummary>;

export const RelationView = z.object({
  id: z.uuid(),
  kind: RelationKind,
  /** "outgoing": this item is `from` (e.g. "blocks X"); "incoming": e.g. "blocked by X". */
  direction: z.enum(["outgoing", "incoming"]),
  item: ItemSummary,
});
export type RelationView = z.infer<typeof RelationView>;

export const ExternalLinkView = z.object({
  id: z.uuid(),
  provider: z.string(),
  kind: z.string(),
  url: z.string(),
  externalId: z.string(),
  title: z.string().nullable(),
  state: z.string().nullable(),
});
export type ExternalLinkView = z.infer<typeof ExternalLinkView>;

export const ActivityView = z.object({
  id: z.uuid(),
  kind: z.string(),
  actorId: UserId.nullable(),
  viaUserId: UserId.nullable(),
  source: z.string(),
  changes: z.record(z.string(), z.unknown()),
  createdAt: Timestamp,
});
export type ActivityView = z.infer<typeof ActivityView>;

export const CommentView = z.object({
  id: z.uuid(),
  itemId: ItemId,
  authorId: UserId.nullable(),
  viaUserId: UserId.nullable(),
  source: z.string(),
  bodyMd: z.string(),
  editedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  /** Detected language of the body (null when detection was not confident). Always sent by the server. */
  language: ContentLanguage.nullable().optional(),
});
export type CommentView = z.infer<typeof CommentView>;

export const MilestoneView = z.object({
  id: z.uuid(),
  productId: z.uuid(),
  name: z.string(),
  version: z.string().nullable(),
  description: z.string().nullable(),
  targetDate: DateOnly.nullable(),
  status: MilestoneStatus,
  releasedAt: Timestamp.nullable(),
});
export type MilestoneView = z.infer<typeof MilestoneView>;

export const ItemDetail = ItemLite.extend({
  descriptionMd: z.string(),
  /** Item version at which the description last changed; send it back as `descriptionBaseVersion`. */
  descriptionVersion: z.number().int(),
  type: TypeSummary,
  status: StatusSummary,
  productKey: z.string(),
  assignees: z.array(UserSummary),
  labels: z.array(LabelSummary),
  milestone: MilestoneView.nullable(),
  reporter: UserSummary.nullable(),
  parent: ItemSummary.nullable(),
  children: z.array(ItemSummary),
  relations: z.array(RelationView),
  externalLinks: z.array(ExternalLinkView),
  activity: z.array(ActivityView),
  comments: z.array(CommentView),
  deletedAt: Timestamp.nullable(),
  /** The product's content language (what `contentLanguage: null` inherits). */
  productContentLanguage: ContentLanguage.optional(),
  /** `contentLanguage ?? productContentLanguage`: use it for `lang` attributes and spellcheck. */
  effectiveContentLanguage: ContentLanguage.optional(),
});
export type ItemDetail = z.infer<typeof ItemDetail>;

/** Set-valued field change: ids to add and to remove. */
export const SetOp = <T extends z.ZodType>(id: T) =>
  z.object({ add: z.array(id).optional(), remove: z.array(id).optional() });
export type SetOp<T = string> = { add?: T[]; remove?: T[] };

/** Where to rank an item among the items of its product. Omit both for "at the end". */
export const Placement = z.object({ afterId: ItemId.nullish(), beforeId: ItemId.nullish() });
export type Placement = z.infer<typeof Placement>;

const LINE_BREAKS = /[\p{Cc}\u2028\u2029]+/u;

/** Splits at runs of control characters / line separators and joins the trimmed, non-empty pieces with one space. */
function oneLineTitle(s: string): string {
  return LINE_BREAKS.test(s)
    ? s
        .split(LINE_BREAKS)
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" ")
    : s;
}

/**
 * An item title is one line: runs of control characters (newlines, tabs, NUL, DEL, C1) and Unicode line
 * separators collapse to a single space (so imports and pastes do not fail), then it is trimmed.
 * Titles end up in lists, CSV cells, notification subjects and generated Markdown, none of which can carry a line break.
 */
export const ItemTitle = z.string().overwrite(oneLineTitle).trim().min(1).max(500);

export const ItemCreate = z.object({
  productId: z.uuid(),
  /** Defaults to the built-in "task" type. */
  typeId: z.uuid().optional(),
  /** Defaults to the first backlog-category status (Triage in the default workflow). */
  statusId: z.uuid().optional(),
  title: ItemTitle,
  /** When omitted (not when empty), the type's template is used. */
  descriptionMd: z.string().max(200_000).optional(),
  priority: Priority.optional(),
  parentId: ItemId.nullish(),
  milestoneId: z.uuid().nullish(),
  assigneeIds: z.array(UserId).optional(),
  labelIds: z.array(z.uuid()).optional(),
  estimate: z.number().nonnegative().nullish(),
  startDate: DateOnly.nullish(),
  dueDate: DateOnly.nullish(),
  fields: FieldValues.optional(),
  reporterId: UserId.nullish(),
  /** Explicit rank; otherwise `placement`; otherwise appended at the end. */
  rank: z.string().optional(),
  placement: Placement.optional(),
  /** Explicit content language (stored as a manual choice). Omit to auto-detect from title and description. */
  contentLanguage: ContentLanguage.optional(),
});
export type ItemCreate = z.infer<typeof ItemCreate>;

export const ItemPatch = z.object({
  typeId: z.uuid().optional(),
  statusId: z.uuid().optional(),
  title: ItemTitle.optional(),
  descriptionMd: z.string().max(200_000).optional(),
  /** Item version the edited description was based on. Conflicts if the description changed since. */
  descriptionBaseVersion: z.number().int().optional(),
  priority: Priority.optional(),
  parentId: ItemId.nullable().optional(),
  milestoneId: z.uuid().nullable().optional(),
  estimate: z.number().nonnegative().nullable().optional(),
  startDate: DateOnly.nullable().optional(),
  dueDate: DateOnly.nullable().optional(),
  reporterId: UserId.nullable().optional(),
  /** Merged into the stored values; `null` removes a field. */
  fields: FieldValues.optional(),
  rank: z.string().optional(),
  placement: Placement.optional(),
  archived: z.boolean().optional(),
  labels: SetOp(z.uuid()).optional(),
  assignees: SetOp(UserId).optional(),
  /** Content language override (a manual choice that detection never replaces); null = back to automatic detection. */
  contentLanguage: ContentLanguage.nullable().optional(),
  /** Whole-item optimistic concurrency: conflicts if the item version differs. */
  expectedVersion: z.number().int().optional(),
});
export type ItemPatch = z.infer<typeof ItemPatch>;

/**
 * Label groups are presented to users as Fields: an exclusive group is a single select, any
 * other group a multi select. Ungrouped labels stay plain tags.
 */
export const FIELD_KINDS = ["single", "multi"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];
